import { ChainType, RouteResponse } from "@0xsquid/squid-types";
import { HistoryTransaction } from "../../core/types/history";
import { getHistoryTransactionId } from "../../services/internal/transactionService";
import { AppConfig, SlippageOption } from "../types/config";
import { SwapDirection } from "../types/swap";
import { Timeframe, TokenWithBalance } from "../types/tokens";

export enum QueryKeys {
  All = "all",
  SquidInfo = "squidInfo",
  Chains = "chains",
  Tokens = "tokens",
  Balances = "balances",
  Balance = "balance",
  AllTokensBalance = "allTokensBalance",
  Transactions = "transactions",
  Transaction = "transaction",
  SwapTransactionStatus = "swapTransactionStatus",
  SendTransactionStatus = "sendTransactionStatus",
  RouteApproved = "routeApproved",
  SendTransactionGas = "sendTransactionGas",
  EnsData = "ensData",
  EnsSearch = "ensSearch",
  CoinGeckoPrices = "coinGeckoPrices",
  CosmosAddress = "cosmosAddress",
  HistoricalData = "historicalData",
  TokensData = "tokensData",
  TokenData = "tokenData",
  FiatToCryptoQuote = "fiatToCryptoQuote",
  FiatToCryptoStatus = "fiatToCryptoStatus",
  FiatToCryptoConfig = "fiatToCryptoConfig",
  Country = "country",
  Xrpl = "xrpl",
  XrplTrustLine = "xrplTrustLine",
  IsXrplTrustLineApproved = "isXrplTrustLineApproved",
  XrplAccountActivatedInfo = "xrplAccountActivatedInfo",
  FiatToCryptoPaymentMethods = "fiatToCryptoPaymentMethods",
  Stellar = "stellar",
  StellarTrustLine = "stellarTrustLine",
  IsStellarTrustLineApproved = "isStellarTrustLineApproved",
  StellarAccountActivatedInfo = "stellarAccountActivatedInfo",
  Hedera = "hedera",
  IsHederaTokenAssociated = "isHederaTokenAssociated",
}

export const keys = () => ({
  // ============
  // Base
  // ============
  all: QueryKeys.All,
  squidInfo: () => [QueryKeys.All, QueryKeys.SquidInfo],
  chains: () => [QueryKeys.All, QueryKeys.Chains],
  tokens: () => [QueryKeys.All, QueryKeys.Tokens],
  balances: () => [QueryKeys.All, QueryKeys.Balances],
  transactions: () => [QueryKeys.All, QueryKeys.Transactions],
  xrpl: () => [QueryKeys.All, QueryKeys.Xrpl],
  stellar: () => [QueryKeys.All, QueryKeys.Stellar],
  hedera: () => [QueryKeys.All, QueryKeys.Hedera],
  // ============
  // Tokens
  // ============
  coinGeckoPrices: (coingeckoIds?: string[]) => [
    ...keys().tokens(),
    QueryKeys.CoinGeckoPrices,
    coingeckoIds?.join(","),
  ],

  // ============
  // Token Historical Data
  // ============
  historicalData: (coingeckoId?: string, timeFrame?: Timeframe) => [
    ...keys().tokens(),
    QueryKeys.HistoricalData,
    coingeckoId,
    timeFrame,
  ],
  tokensData: (tokens?: TokenWithBalance[]) => [
    ...keys().tokens(),
    QueryKeys.TokensData,
    tokens?.map((t) => t.coingeckoId).join(","),
  ],
  tokenData: (token?: TokenWithBalance) => [
    ...keys().tokens(),
    QueryKeys.TokenData,
    token?.coingeckoId,
  ],

  // ============
  // Balances
  // ============
  balance: (
    chainId?: number | string,
    tokenAddress?: string,
    userAddress?: string
  ) => [
    ...keys().balances(),
    QueryKeys.Balance,
    chainId,
    tokenAddress,
    userAddress,
  ],
  allTokensBalance: (
    address: string | undefined,
    chainType: ChainType | undefined,
    direction?: SwapDirection
  ) => [
    ...keys().balances(),
    QueryKeys.AllTokensBalance,
    address,
    chainType,
    direction,
  ],

  // ============
  // Transactions
  // ============
  transaction: (
    fromChainId: string | undefined,
    toChainId: string | undefined,
    toTokenAddress: string | undefined,
    fromTokenAddress: string | undefined,
    price: string | undefined,
    slippage: SlippageOption | undefined,
    sourceUserAddress: string | undefined,
    degenMode: boolean | undefined,
    destinationAddress: string | undefined,
    fallbackAddress: string | undefined,
    quoteOnly: boolean | undefined,
    fromChainType: ChainType | undefined,
    preHook: AppConfig["preHook"] | undefined,
    postHook: AppConfig["postHook"] | undefined,
    overrideGasRefundAddress: AppConfig["overrideGasRefundAddress"] | undefined
  ) => [
    ...keys().transactions(),
    QueryKeys.Transaction,
    fromChainId,
    toChainId,
    toTokenAddress,
    fromTokenAddress,
    price,
    slippage,
    sourceUserAddress,
    degenMode,
    destinationAddress,
    fallbackAddress,
    quoteOnly,
    fromChainType,
    preHook,
    postHook,
    overrideGasRefundAddress,
  ],
  swapTransactionStatus: (transactionId: string | undefined) => [
    ...keys().transactions(),
    QueryKeys.SwapTransactionStatus,
    transactionId,
  ],
  fiatToCryptoStatus: (transactionId?: string) => [
    QueryKeys.All,
    QueryKeys.FiatToCryptoStatus,
    transactionId,
  ],
  sendTransactionStatus: (
    txHash: string | undefined,
    chainId: string | undefined
  ) => [
    ...keys().transactions(),
    QueryKeys.SendTransactionStatus,
    txHash,
    chainId,
  ],
  allTransactionsStatus: (transactions: HistoryTransaction[] | undefined) => [
    ...keys().transactions(),
    ...(transactions?.map(getHistoryTransactionId) ?? []),
  ],
  // ============
  // Approval
  // ============
  routeApproved: (
    routeData: RouteResponse["route"] | undefined,
    allowanceInWei: bigint | undefined,
    isAllowanceQueryEnabled: boolean | undefined,
    hasAllowance: boolean | undefined
  ) => [
    ...keys().transactions(),
    QueryKeys.RouteApproved,
    routeData?.params.fromAddress,
    routeData?.params.fromChain,
    routeData?.params.fromToken,
    routeData?.params.fromAmount,
    allowanceInWei?.toString(),
    isAllowanceQueryEnabled,
    hasAllowance,
  ],
  sendTransactionGas: (
    chainId: string | undefined,
    tokenAddress: string | undefined,
    from: string | undefined
  ) => [
    QueryKeys.All,
    QueryKeys.SendTransactionGas,
    chainId,
    tokenAddress,
    from,
  ],

  // ============
  // ENS
  // ============
  ensData: (address?: string) => [QueryKeys.EnsData, address],
  ensSearch: (query?: string) => [QueryKeys.EnsSearch, query],
  // ============
  // Cosmos
  // ============
  cosmosAddress: (chainId: string | undefined) => [
    QueryKeys.All,
    QueryKeys.CosmosAddress,
    chainId,
  ],
  // ============
  // Fiat to Crypto
  // ============
  fiatToCryptoQuote: (
    fiatCurrency: string,
    cryptoCurrencyID: string,
    amount: number,
    region?: string,
    paymentMethod?: string
  ) => [
    "fiat-to-crypto",
    "quote",
    fiatCurrency,
    cryptoCurrencyID,
    amount,
    region,
    paymentMethod,
  ],
  fiatToCryptoConfig: () => ["fiat-to-crypto", "config"],
  country: () => [QueryKeys.All, QueryKeys.Country],
  fiatToCryptoPaymentMethods: (
    fiatCurrency: string,
    cryptoCurrencyID: string,
    region?: string
  ) => [
    "fiat-to-crypto",
    "payment-methods",
    fiatCurrency,
    cryptoCurrencyID,
    region,
  ],
  // ============
  // Xrpl
  // ============
  xrplTrustLine: (
    tokenAddress: string | undefined,
    chainId: string | undefined,
    address: string | undefined
  ) => [
    ...keys().xrpl(),
    QueryKeys.XrplTrustLine,
    tokenAddress,
    chainId,
    address,
  ],
  isXrplTrustLineApproved: (
    address: string | undefined,
    chainId: string | undefined,
    chainType: ChainType | undefined,
    tokenAddress: string | undefined,
    trustLineLimit: string | undefined,
    amountToApprove: bigint | undefined
  ) => [
    ...keys().xrpl(),
    QueryKeys.IsXrplTrustLineApproved,
    address,
    chainId,
    chainType,
    tokenAddress,
    trustLineLimit,
    amountToApprove?.toString(),
  ],
  xrplAccountActivatedInfo: (
    destinationAddress: string | undefined,
    toChainId: string | undefined,
    toChainType: ChainType | undefined
  ) => [
    ...keys().xrpl(),
    QueryKeys.XrplAccountActivatedInfo,
    destinationAddress,
    toChainId,
    toChainType,
  ],
  // ============
  // Stellar
  // ============
  stellarTrustLine: (
    tokenAddress: string | undefined,
    chainId: string | undefined,
    address: string | undefined
  ) => [
    ...keys().stellar(),
    QueryKeys.StellarTrustLine,
    tokenAddress,
    chainId,
    address,
  ],
  isStellarTrustLineApproved: (
    address: string | undefined,
    chainId: string | undefined,
    chainType: ChainType | undefined,
    tokenAddress: string | undefined,
    trustLineLimit: string | undefined,
    amountToApprove: bigint | undefined
  ) => [
    ...keys().stellar(),
    QueryKeys.IsStellarTrustLineApproved,
    address,
    chainId,
    chainType,
    tokenAddress,
    trustLineLimit,
    amountToApprove?.toString(),
  ],
  stellarAccountActivatedInfo: (
    address: string | undefined,
    chainId: string | undefined,
    chainType: ChainType | undefined
  ) => [
    ...keys().stellar(),
    QueryKeys.StellarAccountActivatedInfo,
    address,
    chainId,
    chainType,
  ],
  // ============
  // Hedera
  // ============
  isHederaTokenAssociated: (
    address: string | undefined,
    chainId: string | undefined,
    chainType: ChainType | undefined,
    tokenAddress: string | undefined
  ) => [
    ...keys().hedera(),
    QueryKeys.IsHederaTokenAssociated,
    address,
    chainId,
    chainType,
    tokenAddress,
  ],
});

export const getPrefixKey = (key: QueryKeys) => {
  switch (key) {
    case QueryKeys.All:
      return [key];

    case QueryKeys.Balance:
    case QueryKeys.AllTokensBalance:
      return [...keys().balances(), key];

    case QueryKeys.Transaction:
    case QueryKeys.SwapTransactionStatus:
    case QueryKeys.FiatToCryptoStatus:
    case QueryKeys.SendTransactionStatus:
    case QueryKeys.Transactions:
    case QueryKeys.RouteApproved:
      return [...keys().transactions(), key];

    case QueryKeys.XrplTrustLine:
      return [...keys().xrpl(), key];

    case QueryKeys.StellarTrustLine:
      return [...keys().stellar(), key];

    case QueryKeys.IsHederaTokenAssociated:
      return [...keys().hedera(), key];

    default:
      return [QueryKeys.All, key];
  }
};
